/* The one analytics value-type vocabulary, shared by every edge that writes or renders a type: the
   columns editor and reconciler in cirra, the stat tile's value, and the analytics_panel_update tool
   in the api. It lives here because a writer with a narrower private list silently destroys the
   types it does not know — that is how html columns broke once, and how the tool (which only ever
   sends "html") would erase the date/number/list types the reconciler infers. Add a type here and
   every edge keeps it.

   Naming rule so keys stay coherent across panel types: wherever a displayed thing is an entry
   object the field is `type` (table columns `{ key, type }`, chart series `{ key, show, type }`);
   wherever the pointer is a flat `<x>Key` the type is its sibling `<x>Type` (the stat tile's
   `valueKey` / `valueType`). Every one of those fields carries a value from THIS list and is read
   through normalizeAnalyticsColumnType.

   Labels ship with the values so a type can never exist without a human name for it. */
export const ANALYTICS_COLUMN_TYPE_OPTIONS = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  /* Two percents because a rate arrives on one of two scales and only the author knows which: 12.4
     (already points) or 0.124 (a fraction of one). Each prints as 12.4%; the formatter never guesses
     a scale from the value (2026-10-15: the tool told the model to send fractions while the formatter
     printed them as "0.12%"). */
  { value: 'percent', label: 'Percent (0–100)' },
  { value: 'ratio', label: 'Percent (0–1)' },
  { value: 'currency', label: 'Currency' },
  { value: 'duration', label: 'Duration' },
  { value: 'rank', label: 'Rank' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'object', label: 'Object' },
  { value: 'list', label: 'List' },
  { value: 'html', label: 'HTML' },
  /* Presentational: the cell IS a per-row button (its `label` is the button text, its click is the
     panel event `button:<key>`), never a reading of a value. Kept in the one vocabulary so no writer
     destroys it, and excluded from the VALUE subset below so it can never be offered as what a stat
     tile, tooltip field, or trend value "is". */
  { value: 'button', label: 'Button' }
];

export const ANALYTICS_COLUMN_TYPES = ANALYTICS_COLUMN_TYPE_OPTIONS.map((option) => option.value);

/* The types a VALUE can be — everything a formatter could be handed. `button` is a column whose cell
   is a control, so every surface that types a value (stat valueType, tooltip fields, trend, format
   overrides) draws from this subset instead of re-filtering the full list locally. */
export const ANALYTICS_VALUE_COLUMN_TYPE_OPTIONS = ANALYTICS_COLUMN_TYPE_OPTIONS.filter((option) => option.value !== 'button');

export const ANALYTICS_VALUE_COLUMN_TYPES = ANALYTICS_VALUE_COLUMN_TYPE_OPTIONS.map((option) => option.value);

/* Names other systems use for the same type — app response schemas and DuckDB both speak these. */
const TYPE_ALIASES = {
  integer: 'number',
  float: 'number',
  decimal: 'number',
  double: 'number',
  bigint: 'number',
  datetime: 'date',
  timestamp: 'date',
  array: 'list',
  bool: 'boolean',
  text: 'string',
  varchar: 'string',
  /* Presentation synonyms the model and app schemas both reach for. */
  pct: 'percent',
  percentage: 'percent',
  money: 'currency',
  position: 'rank',
  seconds: 'duration'
};

export const normalizeAnalyticsColumnType = (type) => {
  const normalized = String(type || '')
    .trim()
    .toLowerCase();

  return TYPE_ALIASES[normalized] || normalized;
};

/* WHAT AN ABSENT VALUE MEANS, declared per type instead of guessed at the render site.

   An empty result is not an error — a window with no rows makes SUM and AVG return null — but the
   honest rendering of that null depends on the measure:

     count/sum        → 0. "Total clicks: 0" is true.
     percent/rank/avg → no value. 0 would LIE: "average position 0" reads better than rank 1, and
                        "CTR 0%" claims impressions that were never served.

   `number` and `currency` default to zero because a KPI reading a plain number is overwhelmingly a
   count or a sum; anything whose null is genuinely undefined declares its own type (percent, rank)
   or overrides with `emptyAs`. Nothing here depends on the transform author remembering to guard:
   the display layer decides, so a panel cannot render "null%" however sloppy the code upstream. */
const TYPE_EMPTY_BEHAVIOUR = {
  number: 'zero',
  currency: 'zero',
  percent: 'none',
  ratio: 'none',
  rank: 'none',
  duration: 'none',
  boolean: 'none',
  date: 'none',
  string: 'none',
  object: 'none',
  list: 'none',
  html: 'none',
  button: 'none'
};

export const EMPTY_VALUE_TEXT = '—';

export const resolveAnalyticsEmptyBehaviour = ({ type, emptyAs = '' } = {}) => {
  const override = String(emptyAs || '')
    .trim()
    .toLowerCase();

  if (override === 'zero' || override === 'none') {
    return override;
  }

  return TYPE_EMPTY_BEHAVIOUR[normalizeAnalyticsColumnType(type)] || 'none';
};

/* Is this value actually a finite analytics number? The formatter and the template mock generator
   share this boundary so numeric text from a source has one meaning on both surfaces. It also keeps
   a badly-written transform from putting garbage such as "null%" on a numeric panel. */
export const toFiniteAnalyticsNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }

  return null;
};

export const ANALYTICS_NUMERIC_COLUMN_TYPES = ['number', 'percent', 'ratio', 'currency', 'duration', 'rank'];
const NUMERIC_TYPES = ANALYTICS_NUMERIC_COLUMN_TYPES;

/* The Intl locale every number and date is written with when nothing chooses one, and the date
   style that goes with it: today's output, byte for byte. A page's theme can pick a number format
   and a date order (analyticsThemeOptions.js); each of those choices names the locale that writes
   it, so the registry and the formatter share these two constants rather than each spelling the
   default. The locale is an implementation detail here: the product speaks in samples ("1.234,56",
   "6 Jan 2026"), never in regions. */
export const ANALYTICS_DEFAULT_LOCALE = 'en-US';
export const ANALYTICS_DEFAULT_DATE_STYLE = 'medium';

/* COMPACT NUMBERS. A shortened number is the value scaled to thousands, millions or billions with
   the unit letter Intl's en-US compact notation writes (K, M, B), done here rather than through
   Intl's own compact notation because that notation is per language: German shortens nothing under
   a million ("266.605,99" stays as it is, then "1,25 Mio."), so a page that asked for the comma
   format AND short numbers got long ones (2026-09-08). The scaling is ours and the same everywhere;
   the locale only writes the scaled number's digits. A value that rounds up to the next unit moves
   to it ("999.9K" of 999950 at no decimals is "1M"), the way Intl decided the unit after rounding. */
const COMPACT_UNITS = [
  { divisor: 1e9, unit: 'B' },
  { divisor: 1e6, unit: 'M' },
  { divisor: 1e3, unit: 'K' }
];

const toCompactReading = (value, maximumFractionDigits) => {
  const factor = 10 ** maximumFractionDigits;
  const round = (number) => Math.round(number * factor) / factor;
  let index = COMPACT_UNITS.findIndex((candidate) => Math.abs(value) >= candidate.divisor);

  if (index === -1) {
    return { number: round(value), unit: '' };
  }

  let rounded = round(value / COMPACT_UNITS[index].divisor);

  if (Math.abs(rounded) >= 1000 && index > 0) {
    index -= 1;
    rounded = round(value / COMPACT_UNITS[index].divisor);
  }

  return { number: rounded, unit: COMPACT_UNITS[index].unit };
};

/* `compact` is what an axis tick needs and a cell never wants: $20K across five ticks against
   $20,000.00 in a table. It is an option rather than a second formatter so a compact currency is
   still THE currency rendering, with the symbol and the grouping decided in one place. `locale`
   decides the grouping and decimal marks (1,234.56 against 1.234,56), nothing else. */
const groupDigits = (value, maximumFractionDigits, compact = false, locale = ANALYTICS_DEFAULT_LOCALE) => {
  if (!compact) {
    return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);
  }

  const reading = toCompactReading(value, maximumFractionDigits);

  return `${new Intl.NumberFormat(locale, { maximumFractionDigits }).format(reading.number)}${reading.unit}`;
};

/* A currency, compact or not, in the locale's own arrangement (the symbol before the digits in
   en-US, after them in de-DE). The unit letter of a compact reading has to sit right after the
   digits, not after the symbol, so the arrangement is taken from Intl's parts and the letter is
   inserted after the last digit part. */
const formatCurrency = ({ value, currency, maximumFractionDigits, compact, locale }) => {
  const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits });

  if (!compact) {
    return formatter.format(value);
  }

  const reading = toCompactReading(value, maximumFractionDigits);
  const parts = formatter.formatToParts(reading.number);
  const lastDigitIndex = parts.reduce((found, part, index) => (part.type === 'integer' || part.type === 'fraction' ? index : found), -1);

  return parts.map((part, index) => (index === lastDigitIndex ? `${part.value}${reading.unit}` : part.value)).join('');
};

/* How many items of a list are printed before the rest are counted. A list column exists to say WHO
   or WHAT is behind a number — the five names under a bar, not all 88,403 of them — so the reader
   gets a sample and the size of what they are not seeing. Capped here rather than at each render
   site so a table cell and a chart's hover card cut the same list at the same place.

   The cap is a display bound, not a data bound: the SQL that builds the column should still cut it
   (`list(owner_name)[1:5]`), because a row carrying thousands of names pays for them in every
   payload whether or not anything prints them. */
const LIST_PREVIEW_LIMIT = 5;

/* DATES. A calendar day and an instant are different facts, and the value itself says which.

   - A WRITTEN date or date-time ("2026-08-19", "2026-08-19 15:00:00", "2026-08-19T15:00") names
     wall-clock parts and no zone. It prints exactly as written, wherever the reader sits: its parts
     are rebuilt as a UTC instant and printed in UTC, so no zone or DST shift can move them. Parsing
     "2026-08-19" as an instant showed the PREVIOUS day west of Greenwich; parsing a zone-less hour
     in the reader's zone moved 02:00 on a spring-forward day to 03:00.
   - An INSTANT (an offset or Z on the string, epoch milliseconds, a Date) is a moment, placed in the
     caller's `timeZone`.

   DuckDB writes TIMESTAMP as "2026-08-19 15:00:00" — a space, not a T — and TIMESTAMPTZ with an hour
   offset ("+00"). Only a T used to count as a time, so every DuckDB timestamp printed as its day
   alone and every 3-hour bucket of one day read "Oct 14" (2026-10-15).

   A number is only read as a date at millisecond scale. Anything smaller is unknowable — seconds
   since the epoch, a year, a day count — and inventing a reading for it would print a confident
   wrong date, so it falls through to the digit grouping below.

   Returns { date, hasTime, timeZone, year, hasClock } or null. `timeZone` is 'UTC' for a written
   value (the zone its parts were rebuilt in) and '' for an instant; `year` and `hasClock` (a time of
   day other than midnight) are read in that same zone, so a chart deciding whether its labels need
   a year or a clock agrees with what the formatter will print.

   `acceptLooseText` hands any other string to the runtime's parser. A column DECLARED a date wants
   that; an untyped value being sniffed for a date does not, because the runtime reads "2026" and
   "March 5" as dates too. */
const WRITTEN_DATE_RE = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}(?::?\d{2})?)?)?$/;
const EPOCH_MILLISECONDS_FLOOR = 100000000000;

const toOffsetMinutes = (zone) => {
  if (zone === 'Z') {
    return 0;
  }

  const digits = zone.slice(1).replace(':', '');
  const minutes = Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2) || 0);

  return zone[0] === '-' ? -minutes : minutes;
};

const toInstantReading = (date, hasTime) => (Number.isFinite(date.getTime()) ? { date, hasTime, timeZone: '', year: date.getFullYear(), hasClock: hasTime && date.getHours() + date.getMinutes() + date.getSeconds() > 0 } : null);

const toWrittenReading = (text) => {
  const match = text.match(WRITTEN_DATE_RE);

  if (!match) {
    return undefined;
  }

  const [, year, month, day, hour = '0', minute = '0', second = '0', fraction = '', zone = ''] = match;
  const parts = [Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second), Number(fraction.slice(0, 3).padEnd(3, '0'))];
  const utc = new Date(Date.UTC(...parts));
  /* Round-trip check: "2026-02-31" and "25:00" parse without complaint and roll into the next unit. */
  const isReal = utc.getUTCFullYear() === parts[0] && utc.getUTCMonth() === parts[1] && utc.getUTCDate() === parts[2] && utc.getUTCHours() === parts[3] && utc.getUTCMinutes() === parts[4];
  const hasTime = match[4] !== undefined;

  if (!isReal) {
    return null;
  }

  if (zone) {
    return toInstantReading(new Date(utc.getTime() - toOffsetMinutes(zone) * 60000), true);
  }

  return { date: utc, hasTime, timeZone: 'UTC', year: parts[0], hasClock: parts[3] + parts[4] + parts[5] > 0 };
};

export const toAnalyticsDateReading = (value, { acceptLooseText = true } = {}) => {
  if (value instanceof Date) {
    return toInstantReading(value, true);
  }

  if (typeof value === 'number') {
    return value >= EPOCH_MILLISECONDS_FLOOR ? toInstantReading(new Date(value), true) : null;
  }

  const text = String(value ?? '').trim();
  const written = toWrittenReading(text);

  if (written !== undefined) {
    return written;
  }

  /* Any other spelling ("Aug 19, 2026") is left to the runtime's parser, as an instant. */
  return text && acceptLooseText ? toInstantReading(new Date(text), /\d{1,2}:\d{2}/.test(text)) : null;
};

/* How much of a date is written, by the page's `dateStyle`: `short` is the day without its year
   ("Jan 6"), `medium` the day with it ("Jan 6, 2026", today's), `numeric` all digits in the
   date order's own sequence ("1/6/2026" month first, "06/01/2026" day first). An instant keeps
   its clock in every style; only `compact` (an axis tick) drops it. */
const DATE_STYLE_PARTS = {
  short: { month: 'short', day: 'numeric' },
  medium: { month: 'short', day: 'numeric', year: 'numeric' },
  numeric: { year: 'numeric', month: 'numeric', day: 'numeric' }
};

/* `timeZone` places an INSTANT — analytics dates mean the workspace's zone, which is a thing only
   the caller knows (see AnalyticsFilterTimezoneNote). It is deliberately ignored for a written
   value: that day and hour were rebuilt from their own digits and have no zone to be moved into. `locale`
   orders the parts (month first or day first); `dateStyle` picks how many parts are written. */
const formatDate = ({ value, compact = false, timeZone = '', locale = ANALYTICS_DEFAULT_LOCALE, dateStyle = ANALYTICS_DEFAULT_DATE_STYLE }) => {
  const reading = toAnalyticsDateReading(value);

  /* Unparseable text prints as it came. "Invalid Date" in a KPI tile is worse than the raw cell. */
  if (!reading) {
    return String(value);
  }

  const parts = DATE_STYLE_PARTS[dateStyle] || DATE_STYLE_PARTS[ANALYTICS_DEFAULT_DATE_STYLE];
  const options = compact ? { ...parts, year: undefined } : { ...parts, ...(reading.hasTime ? { hour: 'numeric', minute: '2-digit' } : {}) };

  const zone = reading.timeZone || (reading.hasTime ? timeZone : '');

  return new Intl.DateTimeFormat(locale, { ...options, ...(zone ? { timeZone: zone } : {}) }).format(reading.date);
};

/* A duration is never negative, but a CHANGE in one is, and the change line formats its amount
   through this same branch ("42s faster than last week" is -54s). Clamping at zero printed that
   move as "0s" — a real movement reported as none (2026-08-20). So the magnitude is formatted and
   the sign is carried back out. */
const formatDuration = (totalSeconds) => {
  const sign = totalSeconds < 0 ? '-' : '';
  const seconds = Math.abs(Math.round(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${sign}${hours}h ${String(minutes).padStart(2, '0')}m`;
  }

  if (minutes > 0) {
    return `${sign}${minutes}m ${String(seconds % 60).padStart(2, '0')}s`;
  }

  return `${sign}${seconds}s`;
};

/* THE formatter. One implementation for every surface that shows a typed value — stat tiles, table
   cells, chart axes and hover cards — so the same number reads identically wherever it appears, and
   an absent value follows its type's declared meaning rather than each renderer's local guess.
   Returns display text, always a string.

   Two options exist for AXES rather than for cells, because a strip of ticks is read as one thing:

   - `compact`       $20k over $20,000, so five ticks fit beside a plot. On a date it drops the year
                     and the time, leaving "Aug 19".
   - `fractionDigits` decides the decimals ONCE for every tick. Without it each value decides for
                      itself, which is right for a cell and wrong for an axis — the same axis would
                      print $0, $2, $4.50.

   Neither changes what a value IS, only how much room it is given.

   A third places an instant: `timeZone` is the zone a datetime is printed in, which only the caller
   knows (an analytics page's dates mean the WORKSPACE zone, not the reader's device). Left unset,
   an instant prints in the runtime's own zone. It never applies to a date-only value.

   Three more are the PAGE's, resolved from its theme and style overlay (resolveAnalyticsValueFormat),
   each an Intl locale or a word the registry chose for the page's number format and date order:

   - `numberLocale` the locale whose grouping and decimal marks the digits wear (1,234.56 with
                    en-US, 1.234,56 with de-DE, 1 234,56 with fr-FR). The currency symbol follows
                    the currency, never the locale.
   - `dateLocale`   the locale whose part order a date follows (Jan 6, 2026 with en-US, 6 Jan 2026
                    with en-GB). Month names stay English either way, since both are English locales.
   - `dateStyle`    how much of a date is written: short, medium (today's) or numeric.

   All default to today's output, so a caller with no page (the thread feed) prints what it always
   has. The digits a type decides on (decimals, the percent sign, the duration parts) are the
   type's and do not move with the format. */
export const formatAnalyticsTypedValue = ({ value, type = '', emptyAs = '', currency = 'USD', compact = false, fractionDigits = null, timeZone = '', numberLocale = ANALYTICS_DEFAULT_LOCALE, dateLocale = ANALYTICS_DEFAULT_LOCALE, dateStyle = ANALYTICS_DEFAULT_DATE_STYLE } = {}) => {
  const normalizedType = normalizeAnalyticsColumnType(type);
  /* A list with nothing in it is absent, not a list: an empty aggregate is the same fact as a null
     one, and printing "" would read as a value that happens to be blank. */
  const isAbsent = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length < 1);
  const numeric = NUMERIC_TYPES.includes(normalizedType) ? toFiniteAnalyticsNumber(value) : null;
  /* A numeric type holding something non-numeric is treated as absent: that is the "null%" guard. */
  const isEmpty = isAbsent || (NUMERIC_TYPES.includes(normalizedType) && numeric === null);

  /* A caller-decided digit count wins over the per-value one wherever a branch below has an opinion,
     which is what makes a tick strip agree with itself. */
  const digits = (perValue) => (Number.isFinite(fractionDigits) ? fractionDigits : perValue);

  if (isEmpty) {
    return resolveAnalyticsEmptyBehaviour({ type: normalizedType, emptyAs }) === 'zero' ? formatAnalyticsTypedValue({ value: 0, type: normalizedType, currency, compact, fractionDigits, numberLocale, dateLocale, dateStyle }) : EMPTY_VALUE_TEXT;
  }

  if (normalizedType === 'percent') {
    return `${groupDigits(numeric, digits(2), compact, numberLocale)}%`;
  }

  /* A fraction of one, written as the percent it is. Scaled here and nowhere else, so the axis, the
     hover card and the cell agree on 0.124 reading 12.4%. */
  if (normalizedType === 'ratio') {
    return `${groupDigits(numeric * 100, digits(2), compact, numberLocale)}%`;
  }

  if (normalizedType === 'currency') {
    return formatCurrency({ value: numeric, currency, maximumFractionDigits: digits(Number.isInteger(numeric) ? 0 : 2), compact, locale: numberLocale });
  }

  if (normalizedType === 'duration') {
    return formatDuration(numeric);
  }

  if (normalizedType === 'rank') {
    return groupDigits(numeric, digits(1), compact, numberLocale);
  }

  if (normalizedType === 'number') {
    return groupDigits(numeric, digits(2), compact, numberLocale);
  }

  if (normalizedType === 'boolean') {
    return value === true ? 'Yes' : 'No';
  }

  /* A day, or a moment — the value decides which (see formatDate). Compact drops the year and the
     time, for a tick strip that has room for "Aug 19" and nothing more. */
  if (normalizedType === 'date') {
    return formatDate({ value, compact, timeZone, locale: dateLocale, dateStyle });
  }

  /* A list prints a sample of its items and how many it is not printing. Chosen by the value being
     an array as well as by the declared type, because no other branch here can render one and the
     untyped passthrough below would print plausible-but-wrong text for it ("a,b", unspaced and
     uncapped). Items are formatted untyped: an array carries one type for itself, not one per item,
     so a number inside one still gets its digit grouping and nothing else is claimed about it. */
  if (normalizedType === 'list' || Array.isArray(value)) {
    const items = Array.isArray(value) ? value : [value];
    const shown = items.slice(0, LIST_PREVIEW_LIMIT).map((item) => formatAnalyticsTypedValue({ value: item, currency, numberLocale, dateLocale, dateStyle }));
    const remaining = items.length - shown.length;

    return remaining > 0 ? `${shown.join(', ')} +${groupDigits(remaining, 0, false, numberLocale)} more` : shown.join(', ');
  }

  /* Untyped and text-ish values pass through, and a bare number still gets digit grouping so an
     un-declared count does not regress to 81453. */
  const inferred = toFiniteAnalyticsNumber(value);

  return inferred !== null && typeof value !== 'boolean' ? groupDigits(inferred, digits(2), compact, numberLocale) : String(value);
};
